import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { authService } from '../utils/api';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Eye, EyeOff, LogIn, AlertCircle, Mail, KeyRound, CheckCircle2, ArrowRight } from 'lucide-react';

const RESEND_SECONDS = 60;

// If the user landed on /login because a ProtectedRoute redirected
// them off a deep link (e.g. /courses/123/recordings?shared=xyz from a
// share link), we honour that intent and send them back there after
// sign-in. We only accept SAME-ORIGIN paths and aggressively reject
// anything that could be coerced into a different host.
//
// Attacks we explicitly defeat:
//   - "//evil.com/x"           → scheme-relative URL
//   - "/\\evil.com/x"          → backslash that some browsers normalize
//   - "%2F%2Fevil.com"         → percent-encoded "//"
//   - "javascript:alert(1)"    → no leading "/"
//   - "http://evil.com"        → no leading "/"
// We additionally resolve the candidate against the current origin
// using the URL constructor and require the resulting origin to match
// window.location.origin exactly. This catches every variant the
// browser would normalize before navigation.
const isSafeNextPath = (next) => {
  if (typeof next !== 'string' || next.length === 0 || next.length > 2000) return false;
  if (next[0] !== '/') return false;
  // Block scheme-relative + backslash + encoded-slash tricks before
  // even trying to parse.
  if (next.startsWith('//') || next.startsWith('/\\') || next.startsWith('/%2f') || next.startsWith('/%2F')) return false;
  try {
    const u = new URL(next, window.location.origin);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
};

// Which top-level path prefixes a given role is allowed to land on
// straight from the login screen. We use this to gate the ?next= path
// — without this check, a student who tapped an /admin/* link (e.g.
// from a broadcast email, WhatsApp, or a bookmark) would be sent to
// /login?next=/admin/... and right after signing in get bounced to the
// 403 "غير مصرح" page instead of their own dashboard. We don't try to
// enumerate every nested admin route here — a coarse prefix check is
// enough to keep the user OFF a page they'll be 403'd from. Real RBAC
// still lives in ProtectedRoute server-side checks.
const ROLE_NEXT_PREFIXES = {
  superadmin: ['/admin', '/staff', '/teacher', '/student', '/courses', '/contact', '/profile'],
  admin:      ['/admin', '/staff', '/teacher', '/student', '/courses', '/contact', '/profile'],
  staff:      ['/staff', '/admin/leads', '/admin/registration-forms', '/admin/calendar-events', '/admin/cohorts', '/admin/messages', '/admin/task-board', '/student', '/teacher', '/courses', '/contact', '/profile'],
  manager:    ['/staff', '/admin/leads', '/admin/registration-forms', '/admin/calendar-events', '/admin/cohorts', '/admin/messages', '/admin/task-board', '/student', '/teacher', '/courses', '/contact', '/profile'],
  teacher:    ['/teacher', '/courses', '/contact', '/profile'],
  student:    ['/student', '/courses', '/contact', '/profile'],
  marketing:  [
    '/marketing',
    '/admin/leads',
    '/admin/contacts',
    '/admin/registration-forms',
    '/admin/messages',
    '/admin/tasks',
    '/admin/schedule',
    '/admin/profile',
    '/admin/website/departments',
    '/admin/website/testimonials',
    '/admin/website/news',
    '/admin/website/banners',
    '/admin/website/pages',
    '/admin/website/library',
    '/admin/website/jobs',
    '/admin/website/display-playlists',
    '/contact',
  ],
};

const isNextAllowedForRole = (next, role) => {
  const prefixes = ROLE_NEXT_PREFIXES[role];
  if (!prefixes) return false;
  // Match exact, "/prefix/...", or "/prefix?..." (search). Reject
  // "/prefixfoo" so /admin doesn't match /administration etc.
  return prefixes.some((p) => next === p || next.startsWith(p + '/') || next.startsWith(p + '?'));
};

const navigateByRole = (navigate, user, next) => {
  const rawRole = user?.role?.name ?? user?.role ?? user?.roleName ?? '';
  const role = String(rawRole).toLowerCase();
  if (isSafeNextPath(next) && isNextAllowedForRole(next, role)) {
    return navigate(next);
  }
  switch (role) {
    case 'admin':
    case 'superadmin':
      return navigate('/admin');
    case 'staff':
    case 'manager':
      return navigate('/staff');
    case 'teacher':
      return navigate('/teacher');
    case 'marketing':
      return navigate('/marketing');
    case 'student':
      return navigate('/student');
    default:
      return navigate('/');
  }
};

const PasswordPanel = ({ navigate, nextPath }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  // Default to checked — users want the long-lived 30-day session by
  // default; they can opt out per-login if they're on a shared device.
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.username.trim()) return setError('يرجى إدخال البريد الإلكتروني أو رقم الهاتف');
    if (!formData.password) return setError('يرجى إدخال كلمة المرور');
    setLoading(true);
    try {
      const result = await login(formData.username, formData.password, rememberMe);
      if (result.success) {
        // Carry the ?next= path through the password sign-in path so
        // share-link recipients (and Watch-page session-expired bounces)
        // land back on the recording, not the role dashboard.
        navigateByRole(navigate, result.user, nextPath);
      } else {
        setError(result.message || 'فشل تسجيل الدخول');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'حدث خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/20 text-red-600 rounded-lg text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="username">البريد الإلكتروني أو رقم الهاتف</Label>
        <Input
          id="username"
          type="text"
          value={formData.username}
          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
          placeholder="أدخل بريدك الإلكتروني أو رقم هاتفك"
          className="h-11"
          autoComplete="username"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">كلمة المرور</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            placeholder="أدخل كلمة المرور"
            className="h-11 pl-10"
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute left-1 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="rounded border-gray-300 accent-[#176163]"
          />
          <span className="text-muted-foreground">تذكرني (30 يوماً)</span>
        </label>
        <Link to="/forgot-password" className="inline-flex min-h-11 items-center text-[#176163] hover:underline font-medium">
          نسيت كلمة المرور؟
        </Link>
      </div>

      <Button
        type="submit"
        className="w-full h-11 bg-[#176163] hover:bg-[#176163]/90 text-base gap-2"
        disabled={loading}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            جاري التحميل...
          </span>
        ) : (
          <>
            <LogIn className="h-5 w-5" />
            تسجيل الدخول
          </>
        )}
      </Button>
    </form>
  );
};

const OtpPanel = ({ navigate, nextPath }) => {
  const { loginWithOtp } = useAuth();
  const [step, setStep] = useState('request'); // 'request' | 'verify'
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  // Default to checked — users want the long-lived 30-day session by
  // default; they can opt out per-login if they're on a shared device.
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (resendIn <= 0) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => setResendIn((s) => s - 1), 1000);
    return () => clearInterval(intervalRef.current);
  }, [resendIn]);

  const requestCode = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setInfo('');
    if (!identifier.trim()) return setError('يرجى إدخال البريد الإلكتروني أو رقم الهاتف');
    setLoading(true);
    try {
      await authService.requestOtp(identifier.trim());
      setInfo('إذا كان الحساب موجوداً فقد تم إرسال الرمز إلى بريدك الإلكتروني المسجّل (وواتساب إن كان مُفعّلاً لحسابك)');
      setStep('verify');
      setResendIn(RESEND_SECONDS);
    } catch (err) {
      setError(err?.response?.data?.message || 'فشل إرسال الرمز');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setError('');
    if (code.length < 4) return setError('يرجى إدخال الرمز');
    setLoading(true);
    try {
      // `rememberMe` here gives the same 30-day session as the password tab.
      const result = await loginWithOtp(identifier.trim(), code.trim(), rememberMe);
      if (result.success) {
        navigateByRole(navigate, result.user, nextPath);
      } else {
        setError(result.message || 'الرمز غير صحيح');
      }
    } catch (err) {
      setError(err?.response?.data?.message || 'الرمز غير صحيح أو منتهي الصلاحية');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'request') {
    return (
      <form onSubmit={requestCode} className="space-y-5">
        <div className="text-sm text-muted-foreground bg-[#176163]/5 border border-[#176163]/10 p-3 rounded-lg flex items-start gap-2">
          <Mail className="h-4 w-4 mt-0.5 text-[#176163] shrink-0" />
          <span>سنرسل رمزاً مكوناً من 6 أرقام إلى بريدك الإلكتروني المسجّل لدى الكلية، وعبر واتساب إن كان مُفعّلاً لحسابك.</span>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/20 text-red-600 rounded-lg text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="otp-id">البريد الإلكتروني أو رقم الهاتف</Label>
          <Input
            // No `name` attr and autoComplete="off" so browsers/password
            // managers do NOT pop their saved-username dropdown over the
            // OTP tab. Autofill should only appear on the password tab
            // (which uses autoComplete="username" + "current-password").
            id="otp-id"
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="أدخل بريدك الإلكتروني أو رقم هاتفك"
            className="h-11"
            autoComplete="off"
            data-form-type="other"
          />
        </div>

        <Button type="submit" className="w-full h-11 bg-[#176163] hover:bg-[#176163]/90 text-base gap-2" disabled={loading}>
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              جاري الإرسال...
            </span>
          ) : (
            <>
              <Mail className="h-5 w-5" />
              إرسال الرمز
            </>
          )}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={verifyCode} className="space-y-5">
      {info && (
        <div className="flex items-start gap-2 p-3 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 rounded-lg text-sm">
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{info}</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/20 text-red-600 rounded-lg text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="otp-code">الرمز المكوّن من 6 أرقام</Label>
        <Input
          id="otp-code"
          name="otp-code"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          // `one-time-code` tells browsers/password managers this is an OTP
          // field, NOT a password — so they stop suggesting saved passwords
          // or popping the autofill bar over it.
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="000000"
          className="h-12 text-center text-2xl tracking-[0.5em] font-mono"
          autoFocus
        />
        <p className="text-xs text-muted-foreground">الرمز صالح لمدة 10 دقائق</p>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => setRememberMe(e.target.checked)}
          className="rounded border-gray-300 accent-[#176163]"
        />
        <span className="text-muted-foreground">تذكرني (إبقاء الجلسة لمدة 30 يوماً)</span>
      </label>

      <Button type="submit" className="w-full h-11 bg-[#176163] hover:bg-[#176163]/90 text-base gap-2" disabled={loading || code.length !== 6}>
        {loading ? (
          <span className="flex items-center gap-2">
            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            جاري التحقق...
          </span>
        ) : (
          <>
            <KeyRound className="h-5 w-5" />
            تحقق وادخل
          </>
        )}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => { setStep('request'); setCode(''); setInfo(''); setError(''); }}
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ArrowRight className="h-4 w-4" />
          تغيير المعرّف
        </button>
        <button
          type="button"
          onClick={requestCode}
          disabled={resendIn > 0 || loading}
          className="min-h-11 px-2 text-[#176163] hover:underline font-medium disabled:text-muted-foreground disabled:no-underline"
        >
          {resendIn > 0 ? `إعادة الإرسال (${resendIn}ث)` : 'إعادة إرسال الرمز'}
        </button>
      </div>
    </form>
  );
};

const LoginPage = () => {
  const { settings: siteSettings } = useSiteSettings();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Default to the OTP tab — product preference: passwordless login is
  // the primary path; users who prefer a password are still one click
  // away.
  const [tab, setTab] = useState('otp');
  // ProtectedRoute appends ?next=<encoded URL> when bouncing
  // unauthenticated users off a deep link (e.g. recording share links).
  // Both panels use this to land the user back where they intended.
  const nextPath = searchParams.get('next') || '';

  // Already-authenticated users who land on /login (e.g. clicked an old
  // bookmark, or browser back from inside the app) get bounced straight
  // to their role dashboard — or to ?next= if it was supplied.
  useEffect(() => {
    if (authLoading) return;
    if (user) navigateByRole(navigate, user, nextPath);
  }, [user, authLoading, navigate, nextPath]);

  return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center gap-6 bg-gradient-to-br from-[#176163]/15 via-background to-[#b18b5e]/15 px-4 pt-safe pb-safe relative overflow-x-clip">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#176163]/25 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#b18b5e]/25 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#176163]/5 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 backdrop-blur-sm bg-card/95">
        <CardHeader className="text-center pb-2 pt-8">
          <div className="mx-auto mb-4">
            <img
              src={siteSettings.logoUrl || '/logo.png'}
              alt={siteSettings.siteNameEn || 'Almadina College'}
              className="h-20 w-auto mx-auto drop-shadow-md"
              onError={(e) => { e.target.src = '/logo.png'; }}
            />
          </div>
          <h1 className="text-2xl font-bold text-[#176163]">مرحباً بك</h1>
          <p className="text-sm text-muted-foreground mt-1">
            سجل دخولك للوصول إلى {siteSettings.siteName || 'بوابة كلية المدينة'}
          </p>
        </CardHeader>
        <CardContent className="pt-4 pb-7">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-muted/60">
              <TabsTrigger value="password" className="data-[state=active]:bg-[#176163] data-[state=active]:text-white gap-2">
                <KeyRound className="h-4 w-4" />
                كلمة المرور
              </TabsTrigger>
              <TabsTrigger value="otp" className="data-[state=active]:bg-[#176163] data-[state=active]:text-white gap-2">
                <Mail className="h-4 w-4" />
                رمز عبر البريد
              </TabsTrigger>
            </TabsList>

            {/* Render ONLY the active tab's panel. The default Tabs behaviour
                keeps both panels mounted (just hidden), which means the
                password tab's <input type="password"> stays in the DOM while
                the user is on the OTP tab — Chrome/Edge then pop their
                "save password / autofill" bar over the OTP code field. Gating
                on `tab` removes the password input from the DOM entirely
                while OTP is active and vice-versa. */}
            <TabsContent value="password" className="mt-0">
              {tab === 'password' && <PasswordPanel navigate={navigate} nextPath={nextPath} />}
            </TabsContent>
            <TabsContent value="otp" className="mt-0">
              {tab === 'otp' && <OtpPanel navigate={navigate} nextPath={nextPath} />}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <div className="relative text-center text-sm text-muted-foreground w-full px-4">
        <p>© {new Date().getFullYear()} كلية المدينة — Almadina College</p>
      </div>
    </div>
  );
};

export default LoginPage;
